import {
  type CorePayeeInvoicesSearchInvoicesAndCreditNotesParams,
  type InvoiceOrCreditDto,
  SearchDateTypeId,
  type StatusTypeDto,
} from 'app/api/apiClient/generated/types';
import { creditNoteStatusTranslation, getInvoiceStatusName } from 'app/api/i18n';
import { SkeletonType } from 'app/components/layout/Page/SkeletonLoader';
// eslint-disable-next-line @stylistic/max-len
import HeldInvoicePayeeDialog from 'app/components/shared/HelpDialogWithIconButton/HeldInvoiceDialog/HeldInvoicePayeeDialog';
import Flex from 'app/components/v2/components/Flex';
import Pill from 'app/components/v2/components/Pill';
import SmallBody from 'app/components/v2/components/Text/SmallBody';
import TooltipIcon from 'app/components/v2/components/TooltipIcon';
import { CreditNoteStatusId, PayeeCreditNoteStatusId } from 'app/constants/creditNotes';
import { InvoiceStatusId, PayeeInvoiceOrCreditNoteTypeId } from 'app/constants/invoices';
import { creditNoteStatusConfig, invoiceStatusConfig } from 'app/constants/status';
import { defaultPaginationParams } from 'app/constants/table';
import i18n from 'app/services/i18n';
import { addDays, formatDate } from 'app/utils/lib/dates';
import { getLanguage } from 'app/utils/lib/i18n';
import { formatCurrency } from 'app/utils/lib/number';

export interface PayeeInvoicesQueryParamsWithView extends CorePayeeInvoicesSearchInvoicesAndCreditNotesParams {
  view?: SkeletonType;
}

export const defaultPayeeInvoicesQueryParams: PayeeInvoicesQueryParamsWithView = {
  dateTypeId: SearchDateTypeId.DatePaid,
  endDate: undefined,
  exportReport: undefined,
  isQuickpay: undefined,
  payeeRelationshipId: undefined,
  q: undefined,
  sessionCurrencyId: undefined,
  sortAsc: false,
  sortField: 'paymentDate',
  startDate: undefined,
  statusId: undefined,
  ...defaultPaginationParams,
};

export const renderPayeeInvoicePaymentDate = (invoice: InvoiceOrCreditDto) => {
  const yesterday = addDays(new Date(new Date().setHours(0, 0, 0, 0)), -1);
  if (invoice.isMissingEft && invoice.paymentDate && new Date(invoice.paymentDate) > yesterday) {
    return (
      <Flex gap={2}>
        <SmallBody
          fontSize="inherit"
          fontWeight="inherit"
        >
          {formatDate(invoice.paymentDate)}
        </SmallBody>

        <TooltipIcon
          iconProps={{
            color: 'info',
            name: 'info',
            size: 'xxs',
          }}
          tooltipProps={{
            tooltipLabel: invoice.payor?.name
              ? i18n.t('payee:invoices.dateTooltipName',
                'Pending funds from {{payorName}}', { payorName: invoice.payor.name }) :
              i18n.t('payee:invoices.dateTooltip', 'Pending funds from payor'),
            tooltipPlacement: 'top',
          }}
        />
      </Flex>
    );
  }

  return formatDate(invoice.paymentDate ?? '');
};

export const getInvoiceOrCreditNoteStatus = (invoice: InvoiceOrCreditDto) => {
  if (!invoice.status?.name) {
    return {
      statusName: '',
    };
  }
  const isCreditNote = invoice.type?.id === PayeeInvoiceOrCreditNoteTypeId.CreditNote;

  if (isCreditNote) {
    const statusId: CreditNoteStatusId = Math.abs(invoice.status.id);

    return {
      statusConfig: creditNoteStatusConfig[statusId],
      statusName: creditNoteStatusTranslation[statusId],
    };
  }

  return {
    statusConfig: invoiceStatusConfig[invoice.status.id as InvoiceStatusId],
    statusName: getInvoiceStatusName(invoice.status),
  };
};

export const renderPayeeInvoiceStatus = (invoice: InvoiceOrCreditDto) => {
  const { statusConfig, statusName } = getInvoiceOrCreditNoteStatus(invoice);

  // this is only to make the TS compiler happy
  // status should always be defined and have a name
  if (statusName == null) {
    return null;
  }

  const isCreditNote = invoice.type?.id === PayeeInvoiceOrCreditNoteTypeId.CreditNote;

  if (isCreditNote) {
    return (
      <Pill
        label={statusName}
        statusColor={statusConfig?.pillColor ?? 'blue'}
      />
    );
  }

  return (
    <Flex gap={1}>
      <Pill
        label={statusName}
        statusColor={statusConfig?.pillColor ?? 'blue'}
      />

      {invoice.status?.note && invoice.payeeRelationship
        && invoice.payor?.name
        && statusConfig?.shouldDisplayStatusNote &&
        (
          <HeldInvoicePayeeDialog
            payeeRelationshipId={invoice.payeeRelationship.id}
            payorName={invoice.payor.name}
            statusNote={invoice.status.note}
          />
        )}
    </Flex>
  );
};

export const renderPayeeInvoiceAmount = (invoice: InvoiceOrCreditDto, hasMultiCurrency: boolean): string =>
  hasMultiCurrency && getLanguage() !== 'en-CA'
    // eslint-disable-next-line @stylistic/max-len
    ? `${invoice.payor?.currencyType?.name ?? ''} ${formatCurrency(invoice.netAmountAfterPayeeTermFee, invoice.payor?.currencyType?.name ?? 'USD')}`.trim()
    : formatCurrency(invoice.netAmountAfterPayeeTermFee, invoice.payor?.currencyType?.name ?? 'USD');

export const shouldRenderCreditNoteDate = (status: null | StatusTypeDto) =>
  status?.id === PayeeCreditNoteStatusId.AppliedInFull ||
  status?.id === PayeeCreditNoteStatusId.Outstanding;
